import type { GameEvent, GameMessage, Role } from "@coup/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { play } from "../../sound";
import { useStore } from "../../state/store";

type AnimBody =
  | { t: "coins"; from: string; to: string; amount: number }
  | { t: "reveal"; playerId: string; role: Role }
  | { t: "exile"; playerId: string; role: Role }
  | { t: "bullshit"; by: string; accused: string; role: Role }
  | { t: "block"; by: string; role: Role };

export type Anim = AnimBody & { key: number };

/** How long each blocking animation holds the old state, in ms. */
const DURATION = { coins: 550, reveal: 1000, exile: 650, bullshit: 1200 } as const;
const BLOCK_CALLOUT_MS = 1100;
const MAX_BEHIND = 3;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Plays each snapshot's events in order before showing it. If the queue falls more
 * than a few snapshots behind, it skips straight to the latest.
 */
export function useDisplayedGame() {
  const latest = useStore((s) => s.game)!;
  const [displayed, setDisplayed] = useState(latest);
  const [anims, setAnims] = useState<Anim[]>([]);
  const queue = useRef<GameMessage[]>([]);
  const generation = useRef(0);
  const busy = useRef(false);
  const seen = useRef(latest);
  const keySeq = useRef(0);

  const show = useCallback((a: AnimBody, ms: number) => {
    const anim: Anim = { ...a, key: ++keySeq.current };
    setAnims((list) => [...list, anim]);
    setTimeout(() => setAnims((list) => list.filter((x) => x.key !== anim.key)), ms);
  }, []);

  const playEvents = useCallback(
    async (events: GameEvent[], gen: number) => {
      for (const e of events) {
        if (generation.current !== gen) return;
        switch (e.t) {
          case "coins":
            play("coins");
            show({ t: "coins", from: e.from, to: e.to, amount: e.amount }, DURATION.coins);
            await wait(DURATION.coins);
            break;
          case "reveal":
            play("card");
            show({ t: "reveal", playerId: e.playerId, role: e.role }, DURATION.reveal);
            await wait(DURATION.reveal);
            break;
          case "cardLost":
            play("loss");
            show({ t: "exile", playerId: e.playerId, role: e.role }, DURATION.exile);
            await wait(DURATION.exile);
            break;
          case "bullshit":
            play("bullshit");
            show({ t: "bullshit", by: e.by, accused: e.accused, role: e.role }, DURATION.bullshit);
            await wait(DURATION.bullshit);
            break;
          case "block":
            play("block");
            show({ t: "block", by: e.by, role: e.role }, BLOCK_CALLOUT_MS);
            break;
          case "drew":
            play("card");
            break;
          case "eliminated":
            play("elimination");
            break;
          case "win":
            play("win");
            break;
        }
      }
    },
    [show],
  );

  useEffect(() => {
    if (latest === seen.current) return;
    seen.current = latest;
    queue.current.push(latest);

    if (queue.current.length > MAX_BEHIND) {
      generation.current++;
      queue.current = [];
      busy.current = false;
      setAnims([]);
      setDisplayed(latest);
      return;
    }
    if (busy.current) return;

    busy.current = true;
    const gen = generation.current;
    void (async () => {
      while (queue.current.length && generation.current === gen) {
        const next = queue.current.shift()!;
        await playEvents(next.events, gen);
        if (generation.current !== gen) return;
        setDisplayed(next);
      }
      busy.current = false;
    })();
  }, [latest, playEvents]);

  useEffect(() => () => void generation.current++, []);

  const myTurn = displayed.view.options.t === "declare";
  useEffect(() => {
    if (myTurn) play("turn");
  }, [myTurn]);

  // Soft tick each second in the last 2 s of a window you can respond to.
  const lastTick = useRef(0);
  const onTick = useCallback((msLeft: number) => {
    const second = Math.ceil(msLeft / 1000);
    if (msLeft > 0 && second <= 2 && second !== lastTick.current) {
      lastTick.current = second;
      play("tick");
    }
    if (second > 2) lastTick.current = 0;
  }, []);

  return { game: displayed, anims, onTick };
}
